import { ChangeDetectionStrategy, Component, computed, DestroyRef, OnInit, Signal, TemplateRef, viewChild } from '@angular/core';
import { AppState } from '../../../../../../store/app.state';
import { Store } from '@ngrx/store';
import { EntityTagSelectors } from '@states/entity-tag/entity-tag.selector-types';
import { DatabaseTagsModel } from '@models/database-tags.model';
import { EntityTagActions } from '@states/entity-tag/entity-tag.action-types';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, map, startWith, tap } from 'rxjs';
import { UiPreloaderComponent } from 'src/app/shared/ui-kit/ui-preloader/ui-preloader.component';
import { PreloaderColor } from '@enums/shared.enum';
import { features } from '@consts/text.const';
import { TranslatePipe } from '@ngx-translate/core';
import { UiNoDataComponent } from '@ui';
import { KeyValuePipe, NgFor, NgIf } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { UiFilterChipComponent } from '@ui';
import { MatTooltip } from '@angular/material/tooltip';
import { Location } from '@angular/common';
import { routerSegments } from '@consts/routes';
import { isEqual } from 'lodash';
import { DeviceLocationModel } from 'src/app/pages/location-v2/pages/device/models';
import { UiDatasetTableComponent } from '@ui/lib/ui-kit/ui-dataset-table';
import { createEntitiesFilteredByTagTableConfig, EntityTagCellContext } from './entities-filtered-by-tag-table.util';

@Component({
  selector: 'app-entities-filtered-by-tag',
  imports: [
    NgIf,
    NgFor,
    UiPreloaderComponent,
    TranslatePipe,
    UiNoDataComponent,
    MatSelectModule,
    ReactiveFormsModule,
    KeyValuePipe,
    UiFilterChipComponent,
    MatTooltip,
    UiDatasetTableComponent,
  ],
  templateUrl: './entities-filtered-by-tag.component.html',
  styleUrl: './entities-filtered-by-tag.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EntitiesFilteredByTagComponent implements OnInit {
  public selectIsLoading: Signal<boolean> = this.store$.selectSignal(EntityTagSelectors.selectIsLoading);
  public selectEntities: Signal<DatabaseTagsModel.EntityTagDocument[]> = this.store$.selectSignal(EntityTagSelectors.selectEntitiesByTag);

  public loaderColor = PreloaderColor;
  public noResults = features.tags.noResults;
  public entityTypeLabels = DatabaseTagsModel.EntityTypeLabels;

  public tagId: string | null = null;
  public entityTypesEnum = DatabaseTagsModel.EntityType;

  public entityTypes = new FormControl<number[]>([]);

  protected readonly nameCellTemplate = viewChild<TemplateRef<EntityTagCellContext>>('nameCell');
  protected readonly typeCellTemplate = viewChild<TemplateRef<EntityTagCellContext>>('typeCell');

  public readonly tableConfig = computed(() => {
    const nameTemplate = this.nameCellTemplate();
    const typeTemplate = this.typeCellTemplate();
    if (!nameTemplate || !typeTemplate) return null;

    return createEntitiesFilteredByTagTableConfig({
      nameCellTemplate: nameTemplate,
      typeCellTemplate: typeTemplate,
      entityTypeLabels: this.entityTypeLabels,
      entityTypesEnum: this.entityTypesEnum,
      onRowClick: (row) => this.goToEntityPage({
        entityType: row.entityType,
        tagId: row.tagId,
        entityId: row.entityId,
        buildingId: row.params?.buildingId,
      }),
      onEditAction: (row) => this.naviagateToEditEntityPage(row.entityType, row.entityId),
    });
  });

  constructor(
    private store$: Store<AppState>,
    private route: ActivatedRoute,
    private destroyRef: DestroyRef,
    public location: Location,
    private router: Router,
  ) { }

  ngOnInit(): void {
    this.route.params
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((params) => {
          if (params['tagId']) {
            this.tagId = params['tagId'];
            this.store$.dispatch(EntityTagActions.startLoadingEntitiesByTag({ id: this.tagId }));
          }
        }))
      .subscribe();

    const qp = this.route.snapshot.queryParamMap;

    const curEntityTypes = (qp.get('entityTypes') ?? '')
      .split(',')
      .filter(Boolean)
      .map((x) => Number(x));

    this.entityTypes.setValue(curEntityTypes);

    this.entityTypes.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((entityTypes) => {
      if (this.tagId) {
        this.store$.dispatch(EntityTagActions.startSettingEntityTypesFilter({
          entityTypesFilter: entityTypes,
          id: this.tagId
        }));
      }
    });

    this.entityTypes.valueChanges
      .pipe(
        startWith(this.entityTypes.value ?? []),
        takeUntilDestroyed(this.destroyRef),
        debounceTime(200),
        map((entityTypes) => ({
          entityTypes: Array.isArray(entityTypes) ? entityTypes : [],
        })),
        distinctUntilChanged(isEqual),
      )
      .subscribe(({ entityTypes }) => {
        const queryParams: any = {
          entityTypes: entityTypes && entityTypes.length ? entityTypes.join(',') : null,
        };

        const tree = this.router.createUrlTree([], {
          relativeTo: this.route,
          queryParams,
          queryParamsHandling: 'merge',
        });

        const url = this.router.serializeUrl(tree);
        this.location.replaceState(url);
      });
  }

  public onLoadMore(): void {
    if (this.tagId) {
      this.store$.dispatch(EntityTagActions.getEntitiesByTag({ id: this.tagId }));
    }
  }

  public onRowAction(event: {
    actionId: string;
    row: DatabaseTagsModel.EntityTagDocument;
  }): void {
    if (event.actionId === 'edit') {
      this.naviagateToEditEntityPage(event.row.entityType, event.row.entityId);
    }
  }

  public cleanFilters(): void {
    this.entityTypes.patchValue([]);
  }

  public cleanEntityTypesControl(entityType: number): void {
    const current = this.entityTypes.value ?? [];
    const updated = current.filter((v) => v !== entityType);
    this.entityTypes.patchValue(updated);
  }

  public goToEntityPage(
    {
      entityType,
      tagId,
      entityId,
      buildingId
    }:
      {
        entityType: DatabaseTagsModel.EntityType,
        tagId: string,
        entityId: string,
        buildingId?: string,
      }
  ): void {
    switch (entityType) {
      case DatabaseTagsModel.EntityType.CAMERA:
        this.naviagateToDevicePage(tagId, DeviceLocationModel.DeviceType.CAMERA)
        break;

      case DatabaseTagsModel.EntityType.CORE:
        this.naviagateToDevicePage(tagId, DeviceLocationModel.DeviceType.CORE)
        break;

      case DatabaseTagsModel.EntityType.APPLE_TV:
        this.naviagateToDevicePage(tagId, DeviceLocationModel.DeviceType.APPLE_TV)
        break;

      case DatabaseTagsModel.EntityType.SENSOR:
        this.naviagateToDevicePage(tagId, DeviceLocationModel.DeviceType.SENSOR)
        break;

      case DatabaseTagsModel.EntityType.SPEAKER:
        this.naviagateToDevicePage(tagId, DeviceLocationModel.DeviceType.SPEAKER)
        break;

      case DatabaseTagsModel.EntityType.REMOTE_IO:
        this.naviagateToDevicePage(tagId, DeviceLocationModel.DeviceType.REMOTE_IO)
        break;

      case DatabaseTagsModel.EntityType.EXTERNAL_STORAGE:
        this.naviagateToDevicePage(tagId, DeviceLocationModel.DeviceType.EXTERNAL_STORAGE)
        break;

      case DatabaseTagsModel.EntityType.LOCATION:
        this.naviagateToDevicePage(tagId)
        break;

      case DatabaseTagsModel.EntityType.SUBLOCATION:
        this.naviagateToDevicePage(tagId)
        break;

      case DatabaseTagsModel.EntityType.WALL:
        this.router.navigate(
          [`/${routerSegments.wallV2}/${routerSegments.view}/${entityId}`],
        );
        break;

      case DatabaseTagsModel.EntityType.EVENT:
        this.router.navigate(
          [`/${routerSegments.alertsV2}/${routerSegments.monitoring}`],
          { relativeTo: this.route, queryParams: { alertName: entityId } }
        );
        break;

      case DatabaseTagsModel.EntityType.EVENT_GROUP:
        this.router.navigate(
          [`/${routerSegments.alertsV2}/${routerSegments.alarms}/${routerSegments.edit}`, entityId],
        );
        break;

      case DatabaseTagsModel.EntityType.FLOOR:
        this.router.navigate(
          [`/${routerSegments.location}/${routerSegments.floors}`],
          { relativeTo: this.route, queryParams: { buildingId: buildingId, floorId: entityId} }
        );
        break;

      case DatabaseTagsModel.EntityType.SHELF:
        this.router.navigate(
          [`/${routerSegments.database}/${routerSegments.shelfs}`],
        );
        break;

      case DatabaseTagsModel.EntityType.CASE:
        this.router.navigate(
          [`/${routerSegments.cases}/${routerSegments.view}/${entityId}`],
        );
        break;

      default:
        break;
    }

  }

  private naviagateToDevicePage(tag: string, type?: DeviceLocationModel.DeviceType,): void {
    this.router.navigate(
      [`/${routerSegments.locationV3}/${routerSegments.devices}/${routerSegments.list}`],
      { relativeTo: this.route, queryParams: { types: type, tags: tag } });
  }

  public naviagateToEditEntityPage(entityType: DatabaseTagsModel.EntityType, entityId: string): void {
    switch (entityType) {
      case DatabaseTagsModel.EntityType.EVENT:
        this.router.navigate(
          [`/${routerSegments.alertsV2}/${routerSegments.edit}/${entityId}`],
        );
        break;

      default:
        break;
    }
  }
}
